// =====================================================
// OTP API CLIENT
//
// One thin wrapper around the backend's OTP endpoints. It owns nothing but
// transport — the BASE_URL rule the rest of the app uses, the auth header, and
// the shape each endpoint returns.
//
// EVERY RULE LIVES ON THE SERVER: how long a code is valid, how many wrong
// attempts are allowed, how soon a resend may be asked for, and whether a
// previous code still works. Nothing here decides any of that, and a modified
// client cannot grant itself anything — it can only ask.
//
// Two families of endpoint, because they have different callers:
//   • /api/auth/…  registration + forgot-password. PUBLIC: the user has no
//                  session yet, by definition.
//   • /api/otp/…   bank details + reward withdrawal. AUTHENTICATED: the code
//                  goes to the signed-in account's own address, which the
//                  client never gets to choose.
// =====================================================

import axios from "axios";

export const BASE_URL =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const token = () => localStorage.getItem("token");

// Created per call so it always picks up the freshest token — the same pattern
// referralApi.js and activityApi.js already use.
const authed = () =>
  axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${token()}` },
  });

const open = () => axios.create({ baseURL: BASE_URL });

// The purposes the authenticated endpoints accept. Mirrors the server's
// ALLOWED_PURPOSES (routes/otpRoutes.js) — the server re-checks, so this list
// is for readability, not for safety.
export const OTP_PURPOSE = {
  BANK_ADD: "bank_add",
  BANK_EDIT: "bank_edit",
  REWARD_WITHDRAW: "reward_withdraw",
};

// =====================================================
// REGISTRATION — EMAIL VERIFICATION
// =====================================================

// Part 2, step 4. Resolves to { verified, nextStep, email }, where nextStep is
// "face" for a new account and "login" once the face check is already done.
export const verifyRegistrationOtp = (email, code) =>
  open()
    .post("/api/auth/verify-otp", { email, code })
    .then((r) => r.data);

// Part 4 — issuing a new code invalidates the previous one, server-side.
export const resendRegistrationOtp = (email) =>
  open()
    .post("/api/auth/resend-otp", { email })
    .then((r) => r.data);

// =====================================================
// FORGOT PASSWORD  (Part 6)
//
// Three calls, in this order. The server refuses to let them be taken out of
// order: setPassword needs the ticket that verifyResetOtp returns, and that
// ticket exists only if a correct code was entered.
// =====================================================

export const startPasswordReset = (email) =>
  open()
    .post("/api/auth/forgot-password", { email })
    .then((r) => r.data);

export const verifyResetOtp = (email, code) =>
  open()
    .post("/api/auth/reset-password/verify", { email, code })
    .then((r) => r.data);

export const setNewPassword = ({ email, ticket, newPassword, confirmPassword }) =>
  open()
    .post("/api/auth/reset-password", { email, ticket, newPassword, confirmPassword })
    .then((r) => r.data);

// =====================================================
// AUTHENTICATED ACTIONS — BANK DETAILS, REWARD WITHDRAWAL
//   (Parts 9 and 10 — the SAME OTP system, only the purpose differs)
// =====================================================

// Sends a code to the signed-in account's own email. The address is never
// passed: the server reads it from the account, which is the only thing that
// makes "verify by email" prove anything.
export const requestActionOtp = (purpose) =>
  authed()
    .post("/api/otp/request", { purpose })
    .then((r) => r.data);

// Resolves to { ticket, ticketExpiresAt }. The ticket is single-use and
// short-lived; it is handed to the guarded action (save bank / withdraw) as
// `otpTicket`.
export const verifyActionOtp = (purpose, code) =>
  authed()
    .post("/api/otp/verify", { purpose, code })
    .then((r) => r.data);

// =====================================================
// SHARED ERROR READER
//
// Every OTP endpoint answers a refusal the same way — { message, reason } —
// so the screens do not each invent their own way of reading one.
// =====================================================
export function otpError(err, fallback = "Something went wrong. Please try again.") {
  const data = err && err.response && err.response.data;
  return {
    message: (data && data.message) || (err && err.message) || fallback,
    reason: (data && data.reason) || "",
    attemptsLeft: data && data.attemptsLeft,
    retryAfterSeconds: data && data.retryAfterSeconds,
  };
}
